import {
  CustomButton,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomSelect,
  errorDialog,
  Option,
  successDialog,
  Text,
  useText,
} from '@src/components';
import '@src/styles/balanceUpload/dailyOrder.scss';
import { monthsList } from '@src/store/enums';
import { Alert, Form } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import warning from '@src/assets/icons/icon-profil-warning-dark.svg';
import iconInfo from '@src/assets/icons/icon-info-blue.svg';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  getLoadBalanceDetailsGetPagedList,
  isDetailFilterActive,
  loadBalanceDetailPriceUpdate,
  setBalanceNotIncludedCards,
  setBalanceUploadDetails,
  setSelectedBalanceUploadDetails,
} from '@src/store/slice/balanceUploadDetailSlice';
import {
  getBusinessDayByMonth,
  loadBalancesUpdate,
  setCurrentLoadBalance,
  setIsAllSelectedRowKeys,
  setSelectedRowKeys,
  setDaysStatus,
} from '@src/store/slice/balanceUploadSlice';
import dayjs from '@src/utils/day';
import { isGreaterMaximumLoadedAmount } from '@src/utils/utils';
import CustomCheckbox from '@src/components/CustomCheckbox';
import DailyPrice from './DailyPrice';
import DailyPriceDisabled from './DailyPriceDisabled';
import DayCount from './DayCount';
import { debounce } from 'lodash';
import { applyPriceToDetails, getSelectedBalanceDetails } from './balanceUploadHelpers';

const DailyOrder = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [form] = Form.useForm();
  const currentDate = dayjs();

  const { selectedCustomer } = useSelector((state) => state.customer);
  const { maximumLoadedAmount } = useSelector((state) => state.orderLimits);
  const { balanceUploadDetails, balanceFilterItems, balanceNotIncludedCards, detailTableFilter } =
    useSelector((state) => state.balanceUploadDetail);
  const {
    currentLoadBalance,
    isAllSelectedRowKeys,
    selectedRowKeys,
    getWorkingDays,
    daysStatus,
    currentMonthDayCount,
  } = useSelector((state) => state.balanceUpload);

  const [isShowAlert, setIsShowAlert] = useState(true);
  const [isDateControl, setIsDateControl] = useState(false);
  const [dayValidMessage, setDayValidMessage] = useState('');
  const [totalDayCount, setTotalDayCount] = useState(currentLoadBalance?.dayCount || 0);
  const [dailyPrice, setDailyPrice] = useState(currentLoadBalance.dailyPrice);
  const [totalPrice, setTotalPrice] = useState(0);
  const debouncedSetTotalPrice = useCallback(
    debounce((price) => setTotalPrice(price), 1000),
    [],
  );

  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const maximumLoadingLimitPerCardIs = useText('maximumLoadingLimitPerCardIs');
  useEffect(() => {
    const currentDay = currentDate?.date();
    const currentMonth = currentDate?.month() + 1;
    const controlDate = currentDay > 15;
    setIsDateControl(controlDate);
    const responseOrderMonth =
      currentLoadBalance?.orderMonth !== 0 ? currentLoadBalance?.orderMonth : undefined;
    const orderMonth = !controlDate ? currentMonth : responseOrderMonth || currentMonth;

    let nextM = currentMonth === 12 ? 1 : currentMonth + 1;
    let filterData = monthsList.filter((item) => [currentMonth, nextM].includes(item.value));
    if (filterData.length === 1) {
      setIsDateControl(false);
    }

    if (currentLoadBalance) {
      form.setFieldsValue({
        dayCount: currentLoadBalance?.dayCount,
        dailyPrice: currentLoadBalance?.dailyPrice,
        orderMonth:
          currentLoadBalance.orderMonth === 0 ? orderMonth : currentLoadBalance.orderMonth,
      });

      if (currentLoadBalance.dailyPrice && maximumLoadedAmount) {
        const controlValue = parseFloat(maximumLoadedAmount?.replace(',', '.')) || 0;
        checkIsAlertShow(controlValue, currentLoadBalance.dailyPrice);
      }
    }
    dispatch(getBusinessDayByMonth(currentMonth));
  }, []);

  useEffect(() => {
    let count = 0;

    const areAllDaysFalse =
      !daysStatus?.includeWeekdays &&
      !daysStatus?.includeSaturday &&
      !daysStatus?.includeSunday &&
      !daysStatus?.includeHoliday;

    if (!areAllDaysFalse) {
      if (daysStatus?.includeWeekdays) count += getWorkingDays?.workDays;
      if (daysStatus?.includeSaturday) count += getWorkingDays?.saturday;
      if (daysStatus?.includeSunday) count += getWorkingDays?.sunday;
      if (daysStatus?.includeHoliday)
        count += getWorkingDays?.publicHoliday + getWorkingDays?.bairam;
      setTotalDayCount(count);
    }
  }, [daysStatus, currentLoadBalance, getWorkingDays]);

  const handleDayCountChange = (value) => {
    dispatch(
      setDaysStatus({
        includeWeekdays: false,
        includeSaturday: false,
        includeSunday: false,
        includeHoliday: false,
      }),
    );
    setTotalDayCount(value);
  };

  const handleDailyPriceChange = (value) => {
    dispatch(setCurrentLoadBalance({ dailyPrice: value }));
    setDailyPrice(value);
  };

  useEffect(() => {
    debouncedSetTotalPrice(dailyPrice * totalDayCount);
  }, [dailyPrice, totalDayCount, debouncedSetTotalPrice]);

  const applyToAll = async (onlySelected) => {
    try {
      const values = await form.validateFields();
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: totalDayCount * values.dailyPrice,
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: maximumLoadingLimitPerCardIs.replaceAll('{amount}', maximumLoadedAmount),
        });
        return;
      }
      await dispatch(loadBalancesUpdate({ ...currentLoadBalance, ...values, periodType: 0 }));

      const justCardNumberFilterItems = balanceFilterItems?.map((item) => item.cardNumber);
      const notSelectedCards = balanceNotIncludedCards?.map((item) => item.cardNumber);

      let data = {
        dayCount: totalDayCount,
        price: values.dailyPrice,
        dailyPrice: values.dailyPrice,
        loadBalanceId: currentLoadBalance.id,
        cardNumber: balanceFilterItems?.length === 0 ? null : justCardNumberFilterItems,
        customerId: selectedCustomer?.customerId,
        notIncludedCards:
          !onlySelected || !notSelectedCards?.length ? null : notSelectedCards,
      };
      const res = await dispatch(loadBalanceDetailPriceUpdate(data));

      if (res.payload.isSuccess) {
        successDialog({
          title: <Text t="success" />,
          message: res?.payload.message || transactionSuccessfullyCompleted2,
          onOk: async () => {
            await dispatch(setSelectedRowKeys([]));
            await dispatch(setIsAllSelectedRowKeys(false));
            await dispatch(setBalanceNotIncludedCards([]));
            await dispatch(getLoadBalanceDetailsGetPagedList({ body: {}, pageNumber: 1 }));
          },
        });
      }
    } catch (e) { }
  };

  const submitAll = () => applyToAll(false);

  const submitSelected = async () => {
    // With an active filter, "select all" only covers the filtered rows, so apply to the explicit selection.
    if (isAllSelectedRowKeys && !isDetailFilterActive(detailTableFilter.body)) {
      await applyToAll(true);
      return;
    }

    try {
      const values = await form.validateFields();

      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: totalDayCount * values.dailyPrice,
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: maximumLoadingLimitPerCardIs.replaceAll('{amount}', maximumLoadedAmount),
        });
        return;
      }

      const selectedDetails = getSelectedBalanceDetails(selectedRowKeys, balanceUploadDetails);
      if (selectedDetails.length > 0) {
        await dispatch(
          loadBalancesUpdate({
            ...currentLoadBalance,
            ...values,
            periodType: 0,
            dayCount: totalDayCount,
          }),
        );

        let data = {
          dayCount: totalDayCount,
          price: values.dailyPrice,
          dailyPrice: values.dailyPrice,
          idList: selectedDetails.map((item) => item.id),
          loadBalanceId: currentLoadBalance?.id,
          cardNumber: selectedDetails.map((item) => item.cardNumber),
          customerId: selectedCustomer?.customerId,
        };

        const res = await dispatch(loadBalanceDetailPriceUpdate(data));

        if (res.payload.isSuccess) {
          dispatch(
            setBalanceUploadDetails(
              applyPriceToDetails(
                store.getState().balanceUploadDetail.balanceUploadDetails,
                selectedDetails,
                {
                  price: values.dailyPrice * totalDayCount,
                  dailyPrice: values.dailyPrice,
                  dayCount: totalDayCount,
                },
              ),
            ),
          );
          successDialog({
            title: <Text t="success" />,
            message: res?.payload.message || transactionSuccessfullyCompleted2,
            onOk: async () => {
              await dispatch(setSelectedRowKeys([]));
              await dispatch(setIsAllSelectedRowKeys(false));
              await dispatch(setBalanceNotIncludedCards([]));
              await dispatch(setSelectedBalanceUploadDetails([]));
            },
          });
        }
      } else {
        errorDialog({
          title: <Text t="error" />,
          htmlContent: <Text t="noSelectedRecord2" />,
        });
      }
    } catch (e) { }
  };

  const mountSelectOptions = () => {
    const currentMonth = currentDate?.month() + 1;
    let nextM = currentMonth === 12 ? 1 : currentMonth + 1;

    let filterData = monthsList.filter((item) => [currentMonth, nextM].includes(item.value));

    if (!isDateControl) {
      filterData = monthsList.filter((item) => [currentMonth].includes(item.value));
    }

    return filterData.map((month) => {
      return (
        <Option
          id={`ticketRestaurant-balanceUpload-balanceInformation-dailyOrder-orderMonth-${month.text}-option`}
          key={month.value}
          value={month.value}
        >
          {month.text}
        </Option>
      );
    });
  };

  const checkIsAlertShow = (controlValue, dailyPrice) => {
    if (controlValue > 0 && dailyPrice >= controlValue) {
      setIsShowAlert(false);
    } else {
      setIsShowAlert(true);
    }
  };

  const dayCountValid = (field, value) => {
    if (value === 0) {
      setDayValidMessage('Değer giriniz');
      return Promise.reject(new Error('error'));
    }
    setDayValidMessage('');
    return Promise.resolve();
  };

  const mountChange = useCallback(
    async (mounthId) => {
      try {
        const action = await dispatch(getBusinessDayByMonth(mounthId));
        if (getBusinessDayByMonth.fulfilled.match(action)) {
          form.setFieldsValue({ dayCount: action?.payload?.data.workDays });
        }
        dispatch(
          setDaysStatus({
            includeWeekdays: true,
            includeSaturday: false,
            includeSunday: false,
            includeHoliday: false,
          }),
        );
      } catch (e) { }
    },
    [dispatch],
  );

  return (
    <CustomCollapseCard className="daily-order-card" cardTitle={<Text t="dailyOrder" />}>
      <div className="daily-order-container">
        {isShowAlert && (
          <Alert
            message={
              <div className="alert-message-contain">
                <CustomImage src={warning} />{' '}
                <div className="description">
                  <div dangerouslySetInnerHTML={{ __html: currentLoadBalance.info }} />
                </div>
              </div>
            }
            banner
            showIcon={false}
            className="custom-alert-warning"
            closable
          />
        )}
        <CustomForm
          name="dailyOrderForm"
          className="daily-order-form"
          initialValues={{}}
          autoComplete="off"
          layout="horizontal"
          form={form}
        >
          <div className="form-item">
            <CustomFormItem
              label={<Text t="monthToCreateOrder" />}
              name="orderMonth"
              className="order-month-form-item"
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="ticketRestaurant-balanceUpload-balanceInformation-dailyOrder-orderMonth-select"
                placeholder={useText('choose')}
                height={36}
                onChange={mountChange}
                disabled={!isDateControl}
              >
                {mountSelectOptions()}
              </CustomSelect>
            </CustomFormItem>
            <div className="daily-amount-container">
              <div className="daily-amount-content">
                <CustomFormItem
                  label={<Text t="dailyAmount2" />}
                  name="dailyPrice"
                  className="daily-amount-form-item"
                  rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                >
                  <DailyPrice
                    dailyPrice={dailyPrice}
                    setDailyPrice={setDailyPrice}
                    onChange={handleDailyPriceChange}
                  />
                </CustomFormItem>
                <Alert
                  message={
                    <div className="alert-message-contain">
                      <div>
                        <CustomImage src={iconInfo} />{' '}
                      </div>
                      <div className="description">
                        <Text t="thisAmountIsBasedOnTheNumberOf" />
                        <br /> <Text t="isCalculatedAccordingToTheDailyMealAllowance" />
                      </div>
                    </div>
                  }
                  banner
                  showIcon={false}
                  className="custom-alert-info"
                />
              </div>
              <div style={{ display: 'flex' }}>
                <div className="daily-count-content">
                  <div
                    className="daily-count-content-details"
                    style={{ display: 'flex', gap: '10px' }}
                  >
                    <CustomFormItem
                      label={<Text t="numberOfDays" />}
                      name="dayCount"
                      className="daily-count-form-item"
                      style={{ display: 'flex', marginBottom: '0px' }}
                      rules={[
                        { required: true, message: <Text t="requiredField2" /> },
                        { validator: dayCountValid, message: <Text t={dayValidMessage} /> },
                      ]}
                    >
                      <span
                        className="info-text"
                        style={{ color: '#00a483', fontSize: '14px', margin: 'auto' }}
                      >
                        x
                      </span>
                      <DayCount
                        dayCount={totalDayCount}
                        onChange={handleDayCountChange}
                        maxDayCount={currentMonthDayCount}
                      />
                    </CustomFormItem>
                    <CustomFormItem style={{ marginBottom: '0px' }}>
                      <DailyPriceDisabled totalPrice={totalPrice} />
                    </CustomFormItem>
                  </div>
                  <div className="checkbox-group">
                    <CustomCheckbox
                      checked={daysStatus?.includeWeekdays}
                      onChange={(e) =>
                        dispatch(setDaysStatus({ includeWeekdays: e.target.checked }))
                      }
                    >
                      <Text t="businessDay" />
                    </CustomCheckbox>
                    <CustomCheckbox
                      checked={daysStatus?.includeSaturday}
                      onChange={(e) =>
                        dispatch(setDaysStatus({ includeSaturday: e.target.checked }))
                      }
                    >
                      <Text t="saturday" />
                    </CustomCheckbox>
                    <CustomCheckbox
                      checked={daysStatus?.includeSunday}
                      onChange={(e) => dispatch(setDaysStatus({ includeSunday: e.target.checked }))}
                    >
                      <Text t="sunday" />
                    </CustomCheckbox>
                    <CustomCheckbox
                      checked={daysStatus?.includeHoliday}
                      onChange={(e) =>
                        dispatch(setDaysStatus({ includeHoliday: e.target.checked }))
                      }
                    >
                      <Text t="publicHoliday" />
                    </CustomCheckbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CustomForm>
        <div className="action-buttons">
          <CustomButton
            id="ticketRestaurant-balanceUpload-balanceInformation-dailyOrder-cancel-btn"
            className="cancel-btn"
            onClick={submitSelected}
          >
            <Text t="applyToSelected2" />
          </CustomButton>
          <CustomButton
            id="ticketRestaurant-balanceUpload-balanceInformation-dailyOrder-primary-btn"
            className="primary-btn"
            onClick={submitAll}
          >
            <Text t="applyToAll2" />
          </CustomButton>
        </div>
      </div>
    </CustomCollapseCard>
  );
};

export default DailyOrder;
